import { apiRequest } from '@/utils/api-request';
import { useOrgStore } from '@/stores/org-store';
import {
  CSVQuality,
  DatasetMetadataResponse,
  DatasetTableMetadata,
} from '@/types/source-types';

interface DatasetMetadataError {
  detail: string;
}

const toDatasetTableMetadata = (
  entry: unknown,
): DatasetTableMetadata | null => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const candidate = entry as Record<string, unknown>;
  const tableMetadata: DatasetTableMetadata = {};

  if (typeof candidate.table === 'string') {
    tableMetadata.table = candidate.table;
  }

  if (typeof candidate.filename === 'string') {
    tableMetadata.filename = candidate.filename;
  }

  if (typeof candidate.s3_key === 'string') {
    tableMetadata.s3_key = candidate.s3_key;
  }

  if (typeof candidate.source_key === 'string') {
    tableMetadata.source_key = candidate.source_key;
  }

  if (typeof candidate.csv_key === 'string') {
    tableMetadata.csv_key = candidate.csv_key;
  }

  if (Array.isArray(candidate.columns)) {
    tableMetadata.columns = candidate.columns.filter(
      (column): column is string => typeof column === 'string',
    );
  }

  if (
    candidate.column_name_mapping &&
    typeof candidate.column_name_mapping === 'object'
  ) {
    tableMetadata.column_name_mapping = candidate.column_name_mapping as Record<
      string,
      unknown
    >;
  }

  if (candidate.quality && typeof candidate.quality === 'object') {
    tableMetadata.quality = candidate.quality as CSVQuality;
  }

  return tableMetadata;
};

const extractTables = (payload: unknown): DatasetTableMetadata[] => {
  if (!payload) {
    return [];
  }

  if (Array.isArray(payload)) {
    return payload
      .map((entry) => toDatasetTableMetadata(entry))
      .filter((entry): entry is DatasetTableMetadata => Boolean(entry));
  }

  if (typeof payload === 'object' && 'tables' in payload) {
    const nested = (payload as { tables?: unknown }).tables;
    return extractTables(nested);
  }

  return [];
};

export const getDatasetMetadata = async (
  collectionId: string,
  datasetId: string,
): Promise<DatasetMetadataResponse | DatasetMetadataError> => {
  const orgId = useOrgStore.getState().currentOrg?.orgid;

  if (!orgId) {
    throw new Error('Organization ID is required');
  }

  const response = await apiRequest<
    DatasetMetadataResponse | { tables?: unknown }
  >({
    url: `/collections/${orgId}/${collectionId}/datasets/${datasetId}/metadata`,
    method: 'GET',
    target: 'platform',
  });

  if (response?.detail) {
    return { detail: response.detail };
  }

  const tables = extractTables(response?.data ?? null);
  return { tables };
};
